<template>
    <div>
        <Panel id="object_schemas_panel" :title="$t('FLOW Objects')">
            <div id="object_schemas">
                <div class="tree-container">
                    <ElTree
                        v-if="!$store.showLoadingOverlay && objects.length > 0"
                        :default-expanded-keys="schemas.length === 1 ? schemas.map((x) => x.id) : []"
                        :load="loadNode"
                        :props="props"
                        class="flow-object-tree"
                        lazy
                        node-key="nodeId"
                    >
                        <template #default="{ node, data }">
                            <span class="custom-tree-node">
                                <span class="tree-node-label" :style="{ width: 300 - node.level * 16 + 'px' }">{{
                                    node.label
                                }}</span>
                                <template v-if="node.level !== 1">
                                    <ElTag v-if="data.isCustom" color="#dae2e6">{{
                                        objects
                                            .filter((x) => x.id === data.type)
                                            .map((x) => `${data.schemaName}.${x.name}`)[0]
                                    }}</ElTag>
                                    <ElTag v-else color="#dae2e6">{{ $t(types[data.type]) }}</ElTag>
                                    <ElTag v-if="data.isArray" color="#dae2e6">{{ $t('Array') }}</ElTag>
                                </template>
                            </span>
                        </template>
                    </ElTree>
                </div>
                <div class="item-form">
                    <ul>
                        <li class="form-label-first">
                            {{ $t('Import OpenAPI Document') }}
                        </li>
                        <li><input type="file" @change="readOpenAPIFile" /></li>
                    </ul>
                </div>
            </div>
        </Panel>

        <div v-if="$store.showLoadingOverlay" class="cic-overlay"></div>
    </div>
</template>

<script>
    import { ElTag, ElTree } from 'element-plus';
    import { size } from 'lodash';
    import Panel from '@/components/container/Panel';
    import File from '@/mixins/File';
    import { load, save } from '@/utils/Http';
    import { showErrorMessage } from '@/utils/Notification';
    import '@/css/objectschemas.scss';

    export default {
        name: 'FlowObjects',
        components: {
            Panel,
            ElTag,
            ElTree,
        },
        mixins: [File],
        data() {
            return {
                objects: [],
                props: { label: 'label', isLeaf: 'leaf' },
                schemas: [],
                types: ['Integer', 'String', 'Boolean', 'Float', 'Date'],
            };
        },
        created() {
            this.loadObjects();
        },
        methods: {
            fileOnLoad(data, content, err) {
                if (err) {
                    showErrorMessage(this.$t('Invalid OpenAPI Document'));
                } else {
                    if (!data.info || !data.info.title) {
                        showErrorMessage(this.$t('OpenAPI document is missing a title.'));
                        return;
                    }

                    if (!data.components || !data.components.schemas || size(data.components.schemas) === 0) {
                        showErrorMessage(this.$t('OpenAPI document is missing component schemas.'));
                        return;
                    }

                    if (this.schemas.some((x) => x.name === data.info.title)) {
                        showErrorMessage(this.$t('Object schema with title in OpenAPI document already exists.'));
                        return;
                    }

                    save('flowobjectschemas/add', { doc: content }, () => {
                        this.loadObjects();
                    });
                }
            },

            loadNode(node, resolve) {
                if (node.level === 0) {
                    return resolve(this.schemas.map((x) => ({ label: x.name, nodeId: x.id, id: x.id })));
                } else if (node.level === 1) {
                    return resolve(
                        this.objects
                            .filter((x) => x.schemaId === node.data.id)
                            .map((x) => ({
                                label: x.name,
                                id: x.id,
                                name: node.data.name,
                                type: x.id,
                                isCustom: true,
                                schemaName: node.data.label,
                            })),
                    );
                } else {
                    if (!node.data.isCustom && node.data.type === 4) {
                        return resolve(
                            ['year', 'month', 'day', 'hour', 'minute', 'second'].map((x) => ({
                                label: this.$t(x),
                                name: `${node.data.name}.${x}`,
                                leaf: true,
                                type: 0,
                            })),
                        );
                    }

                    return resolve(
                        this.objects
                            .find((x) => x.id === node.data.id)
                            .members.map((x) => ({
                                label: x.name,
                                name: `${node.data.name}.${x.name}`,
                                leaf: x.isArray || (!x.isCustom && x.dataType !== 4),
                                id: x.dataType,
                                isArray: x.isArray,
                                type: x.dataType,
                                isCustom: x.isCustom,
                                schemaName: node.data.schemaName,
                            })),
                    );
                }
            },

            loadObjects() {
                load('flowobjects/get', {}, (response) => {
                    this.objects = response.objects.sort((a, b) => (a.name > b.name ? 1 : -1));
                    this.schemas = response.schemas.sort((a, b) => (a.name > b.name ? 1 : -1));
                });
            },
        },
    };
</script>
